import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, TrendingUp, TrendingDown, IndianRupee, LogOut, UserPlus,
  CalendarDays, Loader, AlertCircle, Building2,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { analyticsAPI, establishmentAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import EstablishmentCard from './EstablishmentCard';
import './Analytics.css';

// Indigo palette aligned with the rest of the app.
const PIE_COLORS = ['#4F46E5', '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE', '#E0E7FF'];

const fmtINR = (n) => {
  if (!n) return '₹0';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

const fmtINRFull = (n) =>
  n ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

function Kpi({ icon: Icon, label, value, sub, variant = 'indigo' }) {
  return (
    <div className={`an-kpi an-kpi-${variant}`}>
      <div className="an-kpi-icon"><Icon size={20} /></div>
      <div className="an-kpi-body">
        <div className="an-kpi-label">{label}</div>
        <div className="an-kpi-value">{value}</div>
        {sub && <div className="an-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children, right }) {
  return (
    <div className="an-section">
      <div className="an-section-head">
        <div>
          <h3 className="an-section-title">{title}</h3>
          {subtitle && <p className="an-section-subtitle">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [establishments, setEstablishments] = useState([]);
  const [estId, setEstId] = useState(lockedEstId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { if (lockedEstId) setEstId(lockedEstId); }, [lockedEstId]);

  useEffect(() => {
    if (lockedEstId) return;
    establishmentAPI.getAll()
      .then(r => setEstablishments(r.establishments || []))
      .catch(() => {});
  }, [lockedEstId]);

  useEffect(() => {
    setLoading(true);
    setErr('');
    analyticsAPI.getOverview(estId || null, 12)
      .then(setData)
      .catch(e => setErr(e.message || 'Failed to load analytics.'))
      .finally(() => setLoading(false));
  }, [estId]);

  const monthLabel = useMemo(() => {
    if (!data?.last_month?.start) return '';
    const d = new Date(data.last_month.start);
    return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  }, [data]);

  if (loading) {
    return (
      <div className="an-page">
        <div className="an-state-center"><Loader size={28} className="an-spinner" /><p>Loading analytics…</p></div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="an-page">
        <div className="an-state-center an-state-error">
          <AlertCircle size={28} /><p>{err}</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { headline, payroll, leave, trends, org_comparison } = data;
  const deltaPositive = headline.headcount_delta_pct >= 0;
  const DeltaIcon = deltaPositive ? TrendingUp : TrendingDown;

  return (
    <div className="an-page">
      {/* Header */}
      <div className="an-toolbar">
        <div>
          <h1 className="an-title">Analytics</h1>
          <p className="an-subtitle">
            Workforce, payroll, and attendance overview · Last month: <strong>{monthLabel}</strong>
          </p>
        </div>
      </div>

      {!lockedEstId && (
        <EstablishmentCard
          establishments={[{ id: '', name: 'All organizations' }, ...establishments]}
          value={estId}
          onChange={e => setEstId(e.target.value)}
        />
      )}

      {/* Headline KPIs */}
      <div className="an-kpi-grid">
        <Kpi icon={Users} label="Active Employees" value={headline.active_employees}
             sub={<><DeltaIcon size={12} /> {deltaPositive ? '+' : ''}{headline.headcount_delta_pct}% MoM</>}
             variant="indigo" />
        <Kpi icon={UserPlus} label="New Joins (this month)" value={headline.new_joins_this_month}
             variant="emerald" />
        <Kpi icon={LogOut} label="Exits (last month)" value={headline.exits_last_month}
             variant="rose" />
        <Kpi icon={IndianRupee} label="Payroll (last month)" value={fmtINR(payroll.last_month_total)}
             sub={`Avg salary ${fmtINR(payroll.last_month_avg_salary)}`}
             variant="amber" />
        <Kpi icon={IndianRupee} label="YTD Payroll" value={fmtINR(payroll.ytd_total)}
             variant="blue" />
        <Kpi icon={CalendarDays} label="Leave days (last month)" value={leave.total_days_last_month}
             sub={`${leave.absent_days_last_month} absences`}
             variant="purple" />
      </div>

      {/* Trends row */}
      <div className="an-row-2">
        <Section title="Payroll trend" subtitle="Net payable, last 12 months">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trends.payroll} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={fmtINR} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => fmtINRFull(v)} />
              <Bar dataKey="amount" fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        <Section title="Headcount trend" subtitle="Active employees, end of each month">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trends.headcount} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#4F46E5" strokeWidth={2}
                    dot={{ fill: '#4F46E5', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Section>
      </div>

      {/* Leave breakdown + top takers */}
      <div className="an-row-2">
        <Section title="Leave by type" subtitle={`Approved leave in ${monthLabel}`}>
          {leave.by_type.length === 0 ? (
            <div className="an-empty">No approved leave in this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={leave.by_type} dataKey="days" nameKey="type"
                     cx="50%" cy="50%" outerRadius={80} label={e => e.type}>
                  {leave.by_type.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={v => `${v} days`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="Top leave takers" subtitle={`Most leave taken in ${monthLabel}`}>
          {leave.top_takers.length === 0 ? (
            <div className="an-empty">No leave records for this period.</div>
          ) : (
            <table className="an-table">
              <thead><tr><th>Employee</th><th>ID</th><th style={{ textAlign: 'right' }}>Days</th></tr></thead>
              <tbody>
                {leave.top_takers.map(t => (
                  <tr key={t.employee_id}>
                    <td>{t.name || '—'}</td>
                    <td className="an-muted">{t.employee_id}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{t.days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      {/* Payroll highlight + org comparison */}
      <div className="an-row-2">
        <Section title="Largest department by cost" subtitle={`${monthLabel} payroll`}>
          {payroll.largest_department.name ? (
            <div className="an-highlight">
              <Building2 size={36} />
              <div>
                <div className="an-highlight-label">{payroll.largest_department.name}</div>
                <div className="an-highlight-value">{fmtINRFull(payroll.largest_department.amount)}</div>
                <div className="an-muted">
                  {((payroll.largest_department.amount / payroll.last_month_total) * 100 || 0).toFixed(1)}%
                  {' '}of last month's payroll
                </div>
              </div>
            </div>
          ) : (
            <div className="an-empty">No department data for this period.</div>
          )}
        </Section>

        {org_comparison.length > 0 && (
          <Section title="Organizations" subtitle="Headcount and last-month payroll">
            <table className="an-table">
              <thead><tr><th>Name</th><th style={{ textAlign: 'right' }}>Headcount</th><th style={{ textAlign: 'right' }}>Payroll</th></tr></thead>
              <tbody>
                {org_comparison.map(o => (
                  <tr key={o.id}>
                    <td>{o.name}</td>
                    <td style={{ textAlign: 'right' }}>{o.headcount}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtINR(o.last_month_payroll)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}
      </div>
    </div>
  );
}
